import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { NotificationBellIcon } from "../../widgets/icons";
import { defaultPfpUrl } from "../utils";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { useUserRealtime } from "../../context/UserRealtimeContext";
import { accountHomePath } from "../../lib/sessionToken";
import { MobileNavButton } from "./LeftSidebar";
import { BrandLogo } from "./BrandLogo";
import { GlobalSearch } from "./GlobalSearch";
import HeaderDropdownPortal from "./HeaderDropdownPortal";

/**
 * Logged-in top bar: global search, notifications, user pill.
 *
 * @param {Object} props
 * @param {number} props.userId
 * @param {object|null} props.user
 * @param {import("../../lib/sessionToken").SavedAccount[]} [props.savedAccounts]
 * @param {string} [props.activeAccountKey]
 * @param {(account: import("../../lib/sessionToken").SavedAccount) => Promise<void>} [props.onSwitchAccount]
 * @param {() => Promise<void>} [props.onSignOut]
 * @param {() => Promise<void>} [props.onSignOutAll]
 * @param {() => void} props.onMobileNavOpen
 * @returns {JSX.Element}
 */
function AssemblyTopBar({
  userId,
  user,
  onMobileNavOpen,
  savedAccounts = [],
  activeAccountKey = "",
  onSwitchAccount,
  onSignOut,
  onSignOutAll,
}) {
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownAnchorRef = useRef(null);
  const busyRef = useRef(false);
  const { pendingConnectionCount } = useUserRealtime();

  /**
   * @param {() => Promise<void>|void} action
   * @returns {Promise<void>}
   */
  async function runMenuAction(action) {
    if (busyRef.current) return;
    busyRef.current = true;
    setDropdownOpen(false);
    try {
      await action();
    } finally {
      busyRef.current = false;
    }
  }

  function profilePath() {
    if (user?.type === "organization") {
      return `/organization/${userId}`;
    }
    return `/profile/${userId}`;
  }

  const displayName = user
    ? user.first_name || user.name || user.username || "Me"
    : "Me";
  const initials = displayName.slice(0, 2).toUpperCase();
  const showNotifications = user?.type !== "organization";
  const notificationCount = Number(pendingConnectionCount) || 0;
  const showNotificationBadge = showNotifications && notificationCount > 0;
  const notificationBadgeLabel = notificationCount > 99 ? "99+" : String(notificationCount);

  return (
    <div className="grid h-14 w-full max-w-full min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-1.5 px-2 sm:gap-3 sm:px-6">
      <div className="flex min-w-0 shrink-0 items-center gap-1 justify-self-start sm:gap-2">
        <MobileNavButton onClick={onMobileNavOpen} />
        <BrandLogo />
      </div>

      <div className="flex min-w-0 justify-center overflow-hidden">
        <GlobalSearch className="w-full max-w-xl" />
      </div>

      <div className="flex shrink-0 items-center justify-end justify-self-end gap-0.5 sm:gap-2">
        {showNotifications ? (
          <Button asChild variant="ghost" size="icon" className="relative">
            <Link
              to="/notifications"
              aria-label={
                showNotificationBadge
                  ? `Notifications, ${notificationCount} pending connections`
                  : "Notifications"
              }
            >
              <NotificationBellIcon />
              {showNotificationBadge ? (
                <Badge
                  variant="default"
                  className="absolute right-0.5 top-0.5 min-w-4 justify-center px-1 py-0 text-[9px] leading-3.5"
                >
                  {notificationBadgeLabel}
                </Badge>
              ) : null}
            </Link>
          </Button>
        ) : null}

        <div ref={dropdownAnchorRef}>
          <button
            type="button"
            className="flex items-center rounded-full p-0.5 transition-transform duration-200 hover:scale-105 sm:p-1"
            onClick={() => setDropdownOpen((open) => !open)}
            aria-haspopup="menu"
            aria-expanded={dropdownOpen}
            aria-label={displayName}
          >
            <Avatar className="h-9 w-9">
              <AvatarImage src={user?.pfp_url || defaultPfpUrl} alt={displayName} />
              <AvatarFallback className="bg-tag-accent text-tag-accent-fg text-[11px] font-bold">
                {initials}
              </AvatarFallback>
            </Avatar>
          </button>

          <HeaderDropdownPortal
            open={dropdownOpen}
            anchorRef={dropdownAnchorRef}
            onClose={() => setDropdownOpen(false)}
            role="menu"
            className="w-72 rounded-md border border-ui-border bg-secondary p-1 shadow-lg"
          >
            <div className="max-h-64 overflow-y-auto">
              {savedAccounts.map((account) => {
                const selected = account.key === activeAccountKey;
                const label = account.name || account.username || "Account";
                const accountInitials = label.slice(0, 2).toUpperCase();
                return (
                  <button
                    key={account.key}
                    type="button"
                    role="menuitem"
                    aria-current={selected ? "true" : undefined}
                    className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-text-primary hover:bg-tertiary"
                    onClick={() => runMenuAction(() => {
                      if (onSwitchAccount) return onSwitchAccount(account);
                      navigate(accountHomePath(account.type));
                    })}
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={account.pfp_url || defaultPfpUrl} alt="" />
                      <AvatarFallback className="bg-tag-accent text-tag-accent-fg text-[10px] font-bold">
                        {accountInitials}
                      </AvatarFallback>
                    </Avatar>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{label}</span>
                      {account.username ? (
                        <span className="block truncate text-xs font-normal text-text-secondary">
                          @{account.username}
                        </span>
                      ) : null}
                    </span>
                    {selected ? (
                      <span className="text-text-primary" aria-hidden>
                        ✓
                      </span>
                    ) : (
                      <span className="w-4 shrink-0" aria-hidden />
                    )}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              role="menuitem"
              className="block w-full rounded-md px-3 py-2 text-left text-sm font-medium text-text-primary hover:bg-tertiary"
              onClick={() => runMenuAction(() => navigate("/login"))}
            >
              Add account
            </button>
            <div className="my-1 h-px bg-border-entry" />
            <Link
              to={profilePath()}
              role="menuitem"
              className="block rounded-md px-3 py-2 text-sm font-medium text-text-primary hover:bg-tertiary"
              onClick={() => setDropdownOpen(false)}
            >
              My Profile
            </Link>
            <Link
              to="/settings"
              role="menuitem"
              className="block rounded-md px-3 py-2 text-sm font-medium text-text-primary hover:bg-tertiary"
              onClick={() => setDropdownOpen(false)}
            >
              Settings
            </Link>
            {user?.type !== "organization" ? (
              <Link
                to="/notifications"
                role="menuitem"
                className="block rounded-md px-3 py-2 text-sm font-medium text-text-primary hover:bg-tertiary"
                onClick={() => setDropdownOpen(false)}
              >
                Notifications
              </Link>
            ) : null}
            <div className="my-1 h-px bg-border-entry" />
            <button
              type="button"
              role="menuitem"
              className="block w-full rounded-md px-3 py-2 text-left text-sm font-medium text-error hover:bg-tertiary"
              onClick={() => runMenuAction(() => onSignOut?.())}
            >
              Sign out
            </button>
            {savedAccounts.length > 1 ? (
              <button
                type="button"
                role="menuitem"
                className="block w-full rounded-md px-3 py-2 text-left text-sm font-medium text-error hover:bg-tertiary"
                onClick={() => runMenuAction(() => onSignOutAll?.())}
              >
                Sign out of all accounts
              </button>
            ) : null}
          </HeaderDropdownPortal>
        </div>
      </div>
    </div>
  );
}

export default AssemblyTopBar;
